import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { Home, Clock, GraduationCap, Briefcase, ListChecks, BookOpen, FileUser, Users, LogOut, ShieldCheck, BarChart3, PlayCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Brand";

const NAV = {
  staff: [
    ["/app/staff", "Home", Home], ["/app/attendance", "Clock", Clock], ["/app/academy", "Academy", GraduationCap],
    ["/app/jobs", "Jobs", Briefcase], ["/app/tasks", "Tasks", ListChecks], ["/app/timesheet", "Staff Book", BookOpen],
    ["/app/resume", "Resume", FileUser],
  ],
  employer: [
    ["/app/employer", "Home", Home], ["/app/employer/jobs", "Jobs", Briefcase], ["/app/team", "Team", Users],
    ["/app/train-staff", "Train Staff", GraduationCap], ["/app/tasks", "Tasks", ListChecks], ["/app/timesheet", "Staff Book", BookOpen],
  ],
  admin: [
    ["/app/admin", "Overview", ShieldCheck], ["/app/admin/videos", "Course Videos", PlayCircle], ["/app/admin/content", "Course Content", GraduationCap], ["/app/admin/progress", "Learner Progress", BarChart3], ["/app/employer/jobs", "All Jobs", Briefcase], ["/app/timesheet", "Staff Book", BookOpen],
  ],
};

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (user === null) return <div className="min-h-screen grid place-items-center text-[#5B2A6E]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const items = NAV[user.role] || [];
  const out = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#FAF8FC] lg:flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-[#250E31] text-white sticky top-0 h-screen p-5" data-testid="sidebar">
        <Logo light />
        <nav className="mt-10 space-y-1 flex-1">
          {items.map(([to, label, Icon]) => (
            <NavLink key={to} to={to} end data-testid={`nav-${slug(label)}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#D4AF37] text-[#250E31]" : "text-white/75 hover:bg-white/10"}`}>
              <Icon className="w-4 h-4" />{label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 pt-4">
          <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user.name}</div>
          <div className="text-xs text-[#D4AF37] capitalize">{user.role}{user.company_name ? ` · ${user.company_name}` : ""}</div>
          <button onClick={out} data-testid="logout-btn" className="mt-3 flex items-center gap-2 text-xs text-white/70 hover:text-white"><LogOut className="w-3.5 h-3.5" />Log out</button>
        </div>
      </aside>

      <header className="lg:hidden sticky top-0 z-40 bg-[#250E31]/95 backdrop-blur-md px-4 py-3 flex items-center justify-between">
        <Logo light size={34} sub={false} />
        <button onClick={out} data-testid="logout-btn-mobile" className="text-white/80 p-2" aria-label="Log out"><LogOut className="w-4 h-4" /></button>
      </header>

      <main className="flex-1 min-w-0 px-4 sm:px-8 lg:px-12 py-8 pb-28 lg:pb-12">
        <Outlet />
      </main>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t pb-safe overflow-x-auto" data-testid="bottom-nav">
        <div className="flex min-w-max">
          {items.map(([to, label, Icon]) => (
            <NavLink key={to} to={to} end data-testid={`bottom-nav-${slug(label)}`}
              className={({ isActive }) => `flex-1 min-w-[72px] flex flex-col items-center gap-1 pt-2.5 pb-1 text-[11px] font-medium ${isActive ? "text-[#5B2A6E]" : "text-slate-400"}`}>
              <Icon className="w-5 h-5" />{label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
